/**
 * Focus Trap Utility
 *
 * Consolidates focus trap implementations from:
 * - modal-manager.js (element-level trap)
 * - drawer-controller.js (document-level capturing trap)
 * - tutorial-sandbox.js (element-level trap)
 *
 * Supports both element-level and document-level patterns with a single API.
 *
 * @license GPL-3.0-or-later
 */

/**
 * Default selector for focusable elements
 * Excludes disabled, hidden, and tabindex="-1" elements
 */
const FOCUSABLE_SELECTOR = [
  'button:not([disabled]):not([tabindex="-1"])',
  'a[href]:not([tabindex="-1"])',
  'input:not([disabled]):not([type="hidden"]):not([tabindex="-1"])',
  'select:not([disabled]):not([tabindex="-1"])',
  'textarea:not([disabled]):not([tabindex="-1"])',
  'summary:not([tabindex="-1"])',
  '[tabindex]:not([tabindex="-1"])',
  '[contenteditable="true"]:not([tabindex="-1"])',
].join(', ');

/**
 * Get all focusable elements within a container
 * @param {HTMLElement} container - Container to search within
 * @param {Object} [options] - Options
 * @param {boolean} [options.checkVisibility=true] - Filter to visible elements only
 * @param {boolean} [options.checkAriaHidden=true] - Exclude aria-hidden elements
 * @returns {HTMLElement[]} Array of focusable elements
 */
export function getFocusableElements(container, options = {}) {
  const { checkVisibility = true, checkAriaHidden = true } = options;

  const elements = Array.from(container.querySelectorAll(FOCUSABLE_SELECTOR));

  return elements.filter((el) => {
    // Filter out aria-hidden elements
    if (checkAriaHidden && el.getAttribute('aria-hidden') === 'true') {
      return false;
    }

    // Filter to visible elements only
    if (checkVisibility) {
      const style = window.getComputedStyle(el);
      if (style.display === 'none' || style.visibility === 'hidden') {
        return false;
      }
      // Check if element has layout (offsetWidth/Height or getClientRects)
      // getClientRects catches positioned elements with null offsetParent
      const hasLayout =
        el.offsetWidth > 0 ||
        el.offsetHeight > 0 ||
        el.getClientRects().length > 0;
      if (!hasLayout) {
        return false;
      }
    }

    return true;
  });
}

/**
 * Create an element-level focus trap
 * Attaches a keydown handler to the container element.
 *
 * @param {HTMLElement} container - Container to trap focus within
 * @param {Object} [options] - Options
 * @param {Function} [options.onEscape] - Callback when Escape is pressed (if not provided, Escape is not handled)
 * @returns {Object} Focus trap controller with activate/deactivate methods
 */
export function createFocusTrap(container, options = {}) {
  const { onEscape = null } = options;

  const handleKeydown = (e) => {
    // Handle Escape if callback provided
    if (e.key === 'Escape' && onEscape) {
      e.preventDefault();
      onEscape();
      return;
    }

    if (e.key !== 'Tab') return;

    const focusable = getFocusableElements(container);
    if (focusable.length === 0) return;

    const firstFocusable = focusable[0];
    const lastFocusable = focusable[focusable.length - 1];

    if (e.shiftKey) {
      // Shift + Tab: going backward
      if (document.activeElement === firstFocusable) {
        e.preventDefault();
        lastFocusable.focus();
      }
    } else {
      // Tab: going forward
      if (document.activeElement === lastFocusable) {
        e.preventDefault();
        firstFocusable.focus();
      }
    }
  };

  let isActive = false;

  return {
    /**
     * Activate the focus trap
     * @param {Object} [activateOptions] - Activation options
     * @param {HTMLElement} [activateOptions.initialFocus] - Element to focus on activation
     */
    activate(activateOptions = {}) {
      if (isActive) return;
      isActive = true;
      container.addEventListener('keydown', handleKeydown);

      // Set initial focus at once, not only in the next frame: a busy page,
      // or a tab in the background, can hold that frame back, and a key
      // pressed meanwhile went to whatever was behind the dialog (Escape to
      // the menu that opened it, leaving the dialog open). The frame stays as
      // a retry for a container that only becomes focusable once laid out.
      const target =
        activateOptions.initialFocus || getFocusableElements(container)[0];
      if (target) {
        target.focus();
        requestAnimationFrame(() => {
          if (isActive && !container.contains(document.activeElement)) {
            target.focus();
          }
        });
      }
    },

    /**
     * Deactivate the focus trap
     */
    deactivate() {
      if (!isActive) return;
      isActive = false;
      container.removeEventListener('keydown', handleKeydown);
    },

    /**
     * Check if the trap is currently active
     * @returns {boolean}
     */
    isActive() {
      return isActive;
    },

    /**
     * Get the keydown handler (for backward compatibility)
     * @returns {Function}
     */
    getHandler() {
      return handleKeydown;
    },
  };
}

/**
 * Create a document-level focus trap with focus recovery
 * Attaches a capturing keydown handler to document.
 * Handles focus escaping the container and brings it back.
 *
 * @param {HTMLElement} container - Container to trap focus within
 * @param {Object} [options] - Options
 * @param {Function} [options.onEscape] - Callback when Escape is pressed
 * @param {HTMLElement} [options.fallbackFocus] - Element to focus if no focusables found
 * @param {Function|HTMLElement|HTMLElement[]} [options.alsoTrap] - Extra
 *   container(s) that count as inside the trap, alongside `container`. Pass a
 *   function to have it re-read on every Tab, for a surface that comes and
 *   goes: the mobile Customizer drawer is modal, and a running tutorial's
 *   card sits outside it, reachable to the eye but not by keyboard without
 *   this. Defaults to null: a single container.
 * @returns {Object} Focus trap controller with activate/deactivate methods
 */
export function createDocumentFocusTrap(container, options = {}) {
  const { onEscape = null, fallbackFocus = null, alsoTrap = null } = options;

  /** Every container this trap currently spans, `container` first. */
  const trappedContainers = () => {
    const extra = typeof alsoTrap === 'function' ? alsoTrap() : alsoTrap;
    const list = [container];
    const candidates = Array.isArray(extra) ? extra : extra ? [extra] : [];
    for (const el of candidates) {
      if (el && el !== container && !container.contains(el)) list.push(el);
    }
    return list;
  };

  const handleKeydown = (e) => {
    // Handle Escape
    if (e.key === 'Escape' && onEscape) {
      e.preventDefault();
      onEscape();
      return;
    }

    if (e.key !== 'Tab') return;

    const containers = trappedContainers();
    // Document order, so Tab still walks the page the way it looks.
    const focusable =
      containers.length === 1
        ? getFocusableElements(container)
        : containers
            .flatMap((c) => getFocusableElements(c))
            .sort((a, b) =>
              a.compareDocumentPosition(b) & Node.DOCUMENT_POSITION_FOLLOWING
                ? -1
                : 1
            );
    const effectiveFallback = fallbackFocus || container;

    if (focusable.length === 0) {
      // Keep focus on fallback element when nothing else is focusable
      e.preventDefault();
      if (effectiveFallback.focus) {
        effectiveFallback.focus();
      }
      return;
    }

    const firstElement = focusable[0];
    const lastElement = focusable[focusable.length - 1];
    const active = document.activeElement;
    const inContainer = active
      ? containers.some((c) => c.contains(active))
      : false;

    // Spanning two containers means the browser's own Tab order runs off the
    // end of the first one into whatever the document has in between, and the
    // recovery below only fires on the press after that, so focus would cycle
    // the first container forever and never arrive. With more than one
    // container the trap drives Tab itself rather than nudging the browser's
    // attempt.
    if (containers.length > 1) {
      e.preventDefault();
      const index = focusable.indexOf(active);
      if (index === -1) {
        (e.shiftKey ? lastElement : firstElement).focus();
        return;
      }
      // `getFocusableElements` is a selector's opinion about what can take
      // focus, and it is sometimes wrong. Driving Tab by hand means one
      // element that refuses `.focus()` would pin the walk there forever (a
      // param group's summary can). Confirm the move landed, and step past
      // whatever will not take it.
      const size = focusable.length;
      const direction = e.shiftKey ? -1 : 1;
      for (let hop = 1; hop <= size; hop++) {
        const candidate =
          focusable[(((index + direction * hop) % size) + size) % size];
        if (candidate === active) break;
        candidate.focus();
        if (document.activeElement === candidate) return;
      }
      return;
    }

    // If focus is outside the container, bring it back in
    if (!inContainer) {
      e.preventDefault();
      (e.shiftKey ? lastElement : firstElement).focus();
      return;
    }

    // Cycle within the container
    if (e.shiftKey) {
      if (active === firstElement) {
        e.preventDefault();
        lastElement.focus();
      }
    } else {
      if (active === lastElement) {
        e.preventDefault();
        firstElement.focus();
      }
    }
  };

  let isActive = false;

  return {
    /**
     * Activate the focus trap
     * @param {Object} [activateOptions] - Activation options
     * @param {HTMLElement} [activateOptions.initialFocus] - Element to focus on activation
     * @param {number} [activateOptions.initialFocusDelay=0] - Delay before setting initial focus
     */
    activate(activateOptions = {}) {
      if (isActive) return;
      isActive = true;
      document.addEventListener('keydown', handleKeydown, true);

      // Set initial focus
      const delay = activateOptions.initialFocusDelay || 0;
      const setFocus = () => {
        if (activateOptions.initialFocus) {
          activateOptions.initialFocus.focus();
        } else {
          const focusable = getFocusableElements(container);
          if (focusable.length > 0) {
            focusable[0].focus();
          } else if (fallbackFocus?.focus) {
            fallbackFocus.focus();
          }
        }
      };

      if (delay > 0) {
        setTimeout(setFocus, delay);
      } else {
        // At once, not only in the next frame: a busy page, or a tab in the
        // background, can hold that frame back, and a key pressed meanwhile
        // went to whatever was behind the dialog (Escape to an open menu,
        // leaving the dialog open). The frame stays as a retry for a
        // container that only becomes focusable once it is laid out.
        setFocus();
        requestAnimationFrame(() => {
          if (isActive && !container.contains(document.activeElement)) {
            setFocus();
          }
        });
      }
    },

    /**
     * Deactivate the focus trap
     */
    deactivate() {
      if (!isActive) return;
      isActive = false;
      document.removeEventListener('keydown', handleKeydown, true);
    },

    /**
     * Check if the trap is currently active
     * @returns {boolean}
     */
    isActive() {
      return isActive;
    },
  };
}

/**
 * Simple focus trap function (backward compatible)
 * Returns a keydown handler that can be attached to an element.
 *
 * @param {HTMLElement} container - Container to trap focus within
 * @returns {Function} Keydown handler function
 */
export function trapFocusHandler(container) {
  return createFocusTrap(container).getHandler();
}

/**
 * Disable a button without dropping focus on the page body. A focused button
 * that becomes disabled loses focus, and a keyboard or screen reader user
 * loses their place, so focus moves to the fallback first.
 *
 * @param {HTMLButtonElement|null} button - The button to disable
 * @param {HTMLElement|null} fallback - Where focus goes if the button had it
 */
export function disableKeepingFocus(button, fallback) {
  if (!button) return;
  if (
    document.activeElement === button &&
    typeof fallback?.focus === 'function'
  ) {
    fallback.focus();
  }
  button.disabled = true;
}

/**
 * The first parameter control a person can reach: the first input on show,
 * else the first group's heading when every group is folded (an input in a
 * folded group is in the page, but focus() on it does nothing).
 * @param {HTMLElement|null} container - The parameters container
 * @returns {HTMLElement|null}
 */
export function firstParameterControl(container) {
  if (!container) return null;
  const drawn = (el) =>
    typeof el.checkVisibility !== 'function' || el.checkVisibility();
  return (
    [
      ...container.querySelectorAll(
        'input:not([type="hidden"]), select, textarea'
      ),
    ].find(drawn) ||
    [...container.querySelectorAll('summary')].find(drawn) ||
    null
  );
}

/**
 * Mark a button busy with its own action, or no longer. Not `disabled`: a
 * disabled button loses focus to the page body, and the person who pressed
 * it loses their place. `aria-disabled` keeps it focusable and says it is
 * unavailable (`data-busy` gives it the disabled look); its click handler
 * checks isBusy() and ignores a second press.
 *
 * @param {HTMLButtonElement|null} button
 * @param {boolean} busy
 */
export function setBusy(button, busy) {
  if (!button) return;
  if (busy) {
    button.setAttribute('aria-disabled', 'true');
    button.dataset.busy = 'true';
  } else {
    button.removeAttribute('aria-disabled');
    delete button.dataset.busy;
  }
}

/**
 * Whether a button is busy with its own action (see setBusy).
 * @param {Element|null} button
 * @returns {boolean}
 */
export function isBusy(button) {
  return button?.dataset?.busy === 'true';
}
